// ES (02.11.2026) + correction (user 02.11.2026): an incoming invoice's notes layer – escompte box while it has an escompte,
// „Payée“ stamp once paid – lies over EVERY preview of its PDF (list, Bezahlung, big view), and the preview's „Drucken“ /
// „Herunterladen“ deliver the invoice with the notes. RimoPreview's switch „Vermerke“ hides them; switched off, its buttons
// give the unchanged file (the original only on purpose). Other documents (GET layer 404) or no notes: the plain preview.
import { useCallback, useEffect, useState } from 'react';
import { LayerView } from '@rimocore/shared/overlay/LayerView';
import type { LayerItem } from '@rimocore/shared/overlay/overlay';
import type { PreviewLayerControl, PreviewOverlay } from '@rimocore/shared/preview/RimoPreview';
import { level, session } from '../../api/auth';
import { errorText } from '../../api/client';
import * as A from '../../data/actions';
import { subscribe } from '../../data/compta';
import { tr2 } from '../../i18n/lang';
import { toast } from '../ctx/toast';
import { downloadAnnotated, printAnnotated } from '../docFiles';

const MARKS = { compta: '/assets/rimocompta-mark-papier-144.png' };

/** `editable` = notes can be moved (big view only; needs d_in edit). Null id = no layer. */
export function useDocLayer(documentId: string | null | undefined, editable = false): { overlay?: PreviewOverlay; layer?: PreviewLayerControl; has: boolean } {
  const [doc, setDoc] = useState<A.DocLayer | null>(null);
  const canEdit = editable && level(session.user, 'd_in') === 'EDIT';
  useEffect(() => {
    setDoc(null);
    if (!documentId) return;
    let off = false, t: ReturnType<typeof setTimeout> | undefined;
    const load = () => A.getLayer(documentId).then((d) => { if (!off) setDoc(d); }, () => {});
    void load();
    // Paid, escompte changed …: the live store changes → the notes again (the stamp appears without reopening).
    const un = subscribe(() => { clearTimeout(t); t = setTimeout(() => void load(), 400); });
    return () => { off = true; clearTimeout(t); un(); };
  }, [documentId]);
  const change = useCallback((it: LayerItem) => {
    if (!documentId) return;
    setDoc((d) => d && { ...d, layer: { ...d.layer, items: d.layer.items.map((x) => (x.id === it.id ? it : x)) } });
    A.moveLayerItem(documentId, it.id, it).then(setDoc, (e) => { toast(errorText(e), 'warning'); A.getLayer(documentId).then(setDoc, () => {}); });
  }, [documentId]);
  if (!documentId || !doc?.layer.items.length) return { has: false };
  const labels = { reset: tr2('Zurücksetzen', 'Réinitialiser'), check: tr2('Position prüfen', 'Vérifier la position') };
  return {
    has: true,
    overlay: (p) => <LayerView layer={doc.layer} page={p.page} pageSize={p.size} scale={p.scale} rotation={p.rotation} editable={canEdit} marks={MARKS} labels={labels} onChange={change} />,
    layer: { label: tr2('Vermerke', 'Mentions'), print: () => printAnnotated(documentId), download: () => downloadAnnotated(documentId) },
  };
}
